/**
 * stepConfig — the declarative spec for the Resume Workspace step workflow.
 *
 * Each step is a row in STEP_CONFIG: a stable `key` (matches the run endpoint's
 * step_key), a display `label`, a `group` (steps are rendered grouped, in this
 * order), an optional `prereq` (a step_key that must be done first), and an
 * optional input field (`inputKey` / `inputLabel` / `inputOptional`).
 *
 * `stepState` derives the per-step UI state ("locked" | "available" | "done")
 * purely from the target detail — a step is done if its output exists, locked
 * if its prereq isn't done yet, otherwise available.
 */

import type { ResumeTargetDetail } from "../../api/client";

export interface StepConfig {
  key: string;
  label: string;
  group: string;
  prereq?: string;          // step_key that must be done first
  inputKey?: string;        // inputs.<inputKey> sent to the run endpoint
  inputLabel?: string;      // textarea label
  inputOptional?: boolean;
  minTier?: "all_access";   // gates the step to a plan tier (backend-enforced too)
}

export const STEP_CONFIG: StepConfig[] = [
  { key: "gap_analysis", label: "Gap Analysis", group: "Analyze" },
{ key: "prioritize_plan", label: "Prioritize & Plan", group: "Plan", prereq: "gap_analysis",
    inputKey: "constraints", inputLabel: "Your constraints (hours/week, deadline, budget)" },
  { key: "rewrite_summary", label: "Rewrite Summary", group: "Tailor", prereq: "gap_analysis",
    inputKey: "current_summary", inputLabel: "Your current summary" },
  { key: "rewrite_experience", label: "Rewrite Experience", group: "Tailor", prereq: "gap_analysis",
    inputKey: "experience_entries", inputLabel: "Your experience bullets" },
  { key: "rewrite_skills", label: "Rewrite Skills", group: "Tailor", prereq: "gap_analysis",
    inputKey: "current_skills", inputLabel: "Your current skills section" },
  { key: "target_employer", label: "Target Fit", group: "Target fit", prereq: "gap_analysis" },
  { key: "interview_readiness", label: "Interview Readiness", group: "Interview", minTier: "all_access" },
  { key: "track_monthly", label: "Monthly Check-in", group: "Track", prereq: "prioritize_plan",
    inputKey: "progress_report", inputLabel: "What did you complete this month?" },
  { key: "track_quarterly", label: "Quarterly Recalibration", group: "Track", prereq: "prioritize_plan",
    inputKey: "progress_report", inputLabel: "Progress + market changes this quarter" },
];

export type StepState = "locked" | "available" | "done";

export function stepState(cfg: StepConfig, target: ResumeTargetDetail): StepState {
  if (target.steps[cfg.key]) return "done";
  if (cfg.prereq && !target.steps[cfg.prereq]) return "locked";
  return "available";
}
